import { useCallback, useEffect, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import {
  Button,
  Flex,
  Menu,
  Skeleton,
  useBreakpointValue,
  ItemElementProps,
} from "@nestoca/ui";
import { useAnalytics } from "@shared/analytics";
import { TOAST_AUTOCLOSE_DELAY_IN_MS } from "@shared/constants";
import { useSupportContact } from "@shared/ui";
import { useTranslation } from "react-i18next";
import { PiCheck, PiCopy } from "react-icons/pi";
import { toast } from "react-toastify";

import type { SupportInfoType } from "@shared/ui";

const COPIED_FEEDBACK_DURATION_IN_MS = 1000;
const SKELETON_ROW_IDS = ["skeleton-1", "skeleton-2"];

interface ContactProps {
  applicationId: number;
  isApplicationDashboard?: boolean;
}

export const Contact = ({
  applicationId,
  isApplicationDashboard = false,
}: ContactProps) => {
  const { t } = useTranslation("common");
  const { track } = useAnalytics();

  const isMobile = useBreakpointValue({ default: true, md: false });

  const { supportContactInfo, contactLabel, isLoading } = useSupportContact({
    applicationId,
  });

  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedId) return;

    const timeout = setTimeout(
      () => setCopiedId(null),
      COPIED_FEEDBACK_DURATION_IN_MS
    );

    return () => clearTimeout(timeout);
  }, [copiedId]);

  const copyOption = useCallback(
    async (option: SupportInfoType) => {
      try {
        await navigator.clipboard.writeText(String(option.data));

        setCopiedId(option.id);
        toast(t("contact.valueCopied", { data: option.type }), {
          type: "success",
          autoClose: TOAST_AUTOCLOSE_DELAY_IN_MS,
        });
        track({
          event: "copy_contact_info",
          contact_type: option.id,
        });
      } catch (error) {
        datadogRum.addError(error);
        toast(t("contact.valueCopyFailed", { data: option.type }), {
          type: "error",
          autoClose: TOAST_AUTOCLOSE_DELAY_IN_MS,
        });
      }
    },
    [t, track]
  );

  const button = () => (
    <Button
      variant="secondary"
      size={isMobile ? "small" : "medium"}
      data-dd-action-name="contact"
      e2ePrefix="advisor-contact"
    >
      {t("button.contact")}
    </Button>
  );

  const skeletonItems: ItemElementProps[] = SKELETON_ROW_IDS.map((id) => ({
    id,
    key: id,
    title: <Skeleton animate width={120} />,
    shouldCloseOnSelect: false,
  }));

  // desktop rows copy on press; mobile rows open the tel:/mailto: link
  const contactItems: ItemElementProps[] = supportContactInfo.map((option) => ({
    id: option.id,
    key: option.id,
    textValue: option.data,
    title:
      !isMobile && option.canCopy ? (
        <Flex as="span" align="center" gap={3}>
          {option.data}
          <CopyIcon isCopied={copiedId === option.id} />
        </Flex>
      ) : (
        option.data
      ),
    shouldCloseOnSelect: false,
    e2ePrefix: `contact-${option.id}`,
    ...(isMobile
      ? { href: option.link }
      : { onClick: option.canCopy ? () => copyOption(option) : undefined }),
  }));

  // mobile contact lives in the burger menu, except on the application dashboard
  if (isMobile && !isApplicationDashboard) {
    return null;
  }

  return (
    <Menu
      withSections
      menuItems={[
        {
          // the label is state-dependent, so it stays hidden until the advisor query settles
          sectionHeader: isLoading ? "" : contactLabel,
          sectionAtoms: isLoading ? skeletonItems : contactItems,
        },
      ]}
      selectionMode="none"
      buttonChildren={button}
      ariaLabel={t("button.contact")}
      e2ePrefix="contact"
    />
  );
};

const CopyIcon = ({ isCopied }: { isCopied: boolean }) =>
  isCopied ? (
    <PiCheck aria-hidden color="var(--color-status-success-foreground)" />
  ) : (
    <PiCopy aria-hidden color="var(--color-primary-b-500)" />
  );
